<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import Modal from './modal.vue'
import { defaultConfig } from '../../composables/api'

const route = useRoute()
const router = useRouter()

const props = defineProps({
  filename: {
    type: String,
    required: true,
  },
  format: {
    type: String,
    default: 'pdf',
  },
})

const model = ref(null)
const { user } = storeToRefs(defaultConfig.store_user?.())
/** Отмена устаревших poll при быстрой смене filename. */
let loadGeneration = 0
/** Повторный hidden не должен снова двигать историю. */
let leavingViewer = false
/** Таймер принудительного ухода с пустого /docview. */
let leaveDocShellTimer = 0

/** Remount просмотрщика при смене файла / cache-bust после редактора. */
const viewerKey = computed(
  () => `${props.filename}:${route.query._ || ''}`
)

/** Модалку монтируем только когда upload совпал с route filename. */
const readyUpload = computed(() => {
  const m = model.value
  if (!m?.secure_filename || !props.filename) {
    return null
  }
  if (String(m.secure_filename) !== String(props.filename)) {
    return null
  }
  return m
})

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Метаданные из LTMS backend (архив FK + dockey → сквозные versions).
 *
 * @param {string} filename
 * @returns {Promise<object|null>}
 */
async function fetchBackendUpload(filename) {
  const base = defaultConfig.url_backend
  const token = user.value?.access_token
  if (!base || !filename || !token) {
    return null
  }
  const url = new URL(`upload/${encodeURIComponent(filename)}`, base)
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    cache: 'no-cache',
  })
  if (!resp.ok) {
    return null
  }
  return resp.json()
}

/**
 * Источник истины сразу после save в R7 — docparser /info?path=.
 *
 * @param {string} filename
 * @returns {Promise<object|null>}
 */
async function fetchDocparserInfo(filename) {
  const base = defaultConfig.url_docparse
  const token = user.value?.access_token
  if (!base || !filename || !token) {
    return null
  }
  const url = new URL('info', base)
  url.searchParams.set('path', String(filename))
  url.searchParams.set('service', String(defaultConfig.service || ''))
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    cache: 'no-cache',
  })
  if (!resp.ok) {
    return null
  }
  return resp.json()
}

/**
 * Versions по dockey (полная R7-цепочка), без смены текущего secure_filename.
 *
 * @param {string} dockey
 * @returns {Promise<object|null>}
 */
async function fetchDocparserByDockey(dockey) {
  const base = defaultConfig.url_docparse
  const token = user.value?.access_token
  if (!base || !dockey || !token) {
    return null
  }
  const url = new URL('info', base)
  url.searchParams.set('key', String(dockey))
  url.searchParams.set('service', String(defaultConfig.service || ''))
  const resp = await fetch(url, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    cache: 'no-cache',
  })
  if (!resp.ok) {
    return null
  }
  return resp.json()
}

/**
 * Сквозные 1..N по secure_filename (Архив ↔ просмотрщик после R7).
 *
 * @param {Array} list
 * @returns {Array}
 */
function renumberVersionEntries(list) {
  const byFile = new Map()
  for (const item of list || []) {
    if (!item?.secure_filename) {
      continue
    }
    const prev = byFile.get(item.secure_filename)
    byFile.set(item.secure_filename, {
      secure_filename: item.secure_filename,
      deleted: Boolean(item.deleted),
      uploaded_at: item.uploaded_at || prev?.uploaded_at || null,
      id: item.id ?? prev?.id ?? null,
      version: item.version ?? prev?.version,
    })
  }
  const items = [...byFile.values()].sort((a, b) => {
    const ta = String(a.uploaded_at || '')
    const tb = String(b.uploaded_at || '')
    if (ta && tb && ta !== tb) {
      return ta < tb ? -1 : 1
    }
    const va = Number(a.version || 0)
    const vb = Number(b.version || 0)
    if (va !== vb) {
      return va - vb
    }
    return String(a.secure_filename).localeCompare(String(b.secure_filename))
  })
  return items.map((item, index) => ({
    version: index + 1,
    secure_filename: item.secure_filename,
    deleted: Boolean(item.deleted),
  }))
}

/**
 * Объединяет versions из LTMS (архив) и docparser (dockey R7).
 *
 * @param {...(object|null|undefined)} sources
 * @returns {Array|null}
 */
/**
 * Актуальная версия, если файл из адреса есть в списке, но он не последний.
 * Записи /docview в истории закреплены на файле: «назад»/«вперёд» после R7 save
 * иначе показывали бы старую версию. Файла нет в списке (save ещё не попал
 * в Архив) — адрес новее списка, не трогаем.
 *
 * @param {Array|null} versions версии по uploaded_at (renumberVersionEntries).
 * @param {string} filename secure_filename из адреса.
 * @returns {string|null} secure_filename актуальной версии или null.
 */
function newerLatestFilename(versions, filename) {
  const live = (Array.isArray(versions) ? versions : []).filter(
    v => v?.secure_filename && !v.deleted
  )
  if (!live.some(v => v.secure_filename === filename)) {
    return null
  }
  const latest = live[live.length - 1].secure_filename
  return latest !== filename ? latest : null
}

function mergeVersionSources(...sources) {
  const rows = []
  for (const src of sources) {
    if (!src) {
      continue
    }
    if (Array.isArray(src.versions)) {
      rows.push(...src.versions)
    }
    if (src.secure_filename) {
      rows.push({
        version: src.version,
        secure_filename: src.secure_filename,
        deleted: Boolean(src.deleted),
        uploaded_at: src.uploaded_at,
        id: src.id || src.iddoc,
      })
    }
  }
  const versions = renumberVersionEntries(rows)
  return versions.length ? versions : null
}

/**
 * Нормализует ответ /info|/upload для Modal (нужен author + versions).
 *
 * @param {object} raw
 * @param {string} filename
 * @param {object|null} [extra]
 * @returns {object}
 */
function normalizeUpload(raw, filename, extra = null) {
  const author = raw?.author || extra?.author
  // LTMS entity archive (GET /upload или /uploads/:division) — как модалка Архива.
  const beVersions = Array.isArray(extra?.versions) && extra.versions.length
    ? renumberVersionEntries(extra.versions)
    : null
  let versions = beVersions || mergeVersionSources(extra, raw)
  const secureFilename = raw?.secure_filename || filename
  if (secureFilename && versions?.length) {
    if (!versions.some(v => v.secure_filename === secureFilename)) {
      versions = renumberVersionEntries([
        ...versions,
        {
          version: versions.length + 1,
          secure_filename: secureFilename,
          deleted: Boolean(raw?.deleted ?? extra?.deleted),
          uploaded_at:
            raw?.uploaded_at || extra?.uploaded_at || new Date().toISOString(),
          id: raw?.id || raw?.iddoc || extra?.id || extra?.iddoc,
        },
      ])
    }
  } else if (secureFilename && !versions?.length) {
    versions = renumberVersionEntries([
      {
        version: raw?.version || extra?.version || 1,
        secure_filename: secureFilename,
        deleted: Boolean(raw?.deleted ?? extra?.deleted),
        uploaded_at: raw?.uploaded_at || extra?.uploaded_at,
        id: raw?.id || raw?.iddoc || extra?.id,
      },
    ])
  }
  const base = {
    ...extra,
    ...raw,
    secure_filename: secureFilename,
    filename: raw?.filename || extra?.filename || filename,
    author:
      author && typeof author === 'object'
        ? author
        : { username: typeof author === 'string' ? author : '', fio: '', email: '' },
  }
  if (versions?.length) {
    base.versions = versions
    const self = versions.find(v => v.secure_filename === base.secure_filename)
    if (self?.version != null) {
      base.version = self.version
    }
  }
  return base
}

/**
 * Архив сущности (тот же endpoint, что модалка Архива) — полный 1..N.
 *
 * @param {object|null} meta
 * @param {string|null} division
 * @returns {Promise<object[]|null>}
 */
async function fetchEntityArchiveRows(meta, division) {
  const base = defaultConfig.url_backend
  const token = user.value?.access_token
  const itemId = meta?.item_id
  const model = meta?.model
  const div = division || meta?.division
  if (!base || !token || itemId == null || !model || !div) {
    return null
  }
  const entityMap = {
    Products: 'products',
    Sites: 'sites',
    Taxes: 'taxes',
    Submissions: 'submissions',
    Templates: 'templates',
    GmpStatusList: 'gmp_status_list',
    ServiceNotes: 'service_notes',
  }
  const entity = entityMap[model] || String(model).toLowerCase()
  const url = new URL(`${entity}/${itemId}/uploads/${div}`, base)
  try {
    const resp = await fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      cache: 'no-cache',
    })
    if (!resp.ok) {
      return null
    }
    const body = await resp.json()
    const rows = body?.data
    return Array.isArray(rows) ? rows : null
  } catch (error) {
    console.error('[soykajs] не удалось загрузить архив сущности', error)
    return null
  }
}

/**
 * После редактора: docparser (файл) + LTMS Архив сущности → один список.
 */
async function loadUpload() {
  const filename = props.filename
  const gen = ++loadGeneration
  if (!filename) {
    if (gen === loadGeneration) {
      model.value = null
    }
    return
  }

  // Не сбрасываем model сразу: иначе v-if снимает Modal → @hidden → router.back()
  // и пустой экран на /docview, пока идёт poll после редактора / смены версии.
  if (model.value && String(model.value.secure_filename) !== String(filename)) {
    model.value = null
  }

  const deadline = Date.now() + 4500
  while (Date.now() <= deadline && gen === loadGeneration) {
    let fromDp = null
    let fromBe = null
    try {
      fromDp = await fetchDocparserInfo(filename)
    } catch (error) {
      console.error(
        '[soykajs] не удалось получить info docparser для',
        filename,
        error
      )
    }
    try {
      fromBe = await fetchBackendUpload(filename)
    } catch (error) {
      console.error(
        '[soykajs] не удалось получить upload из LTMS для',
        filename,
        error
      )
    }

    if (gen !== loadGeneration) {
      return
    }

    if (fromDp?.secure_filename || fromBe?.secure_filename) {
      const primary =
        fromDp?.secure_filename === filename
          ? fromDp
          : fromBe?.secure_filename === filename
            ? fromBe
            : fromDp || fromBe

      const meta = normalizeUploadMetadata(
        fromBe?.metadata || fromDp?.metadata || primary?.metadata
      )
      const division = fromBe?.division || fromDp?.division || primary?.division
      let archiveRows = null
      try {
        archiveRows = await fetchEntityArchiveRows(meta, division)
      } catch (error) {
        console.error(
          '[soykajs] не удалось получить версии из архива сущности',
          error
        )
        archiveRows = null
      }
      if (gen !== loadGeneration) {
        return
      }

      const archiveVersions = Array.isArray(archiveRows) && archiveRows.length
        ? renumberVersionEntries(
            archiveRows.map(row => ({
              version: row.version,
              secure_filename: row.secure_filename,
              deleted: Boolean(row.deleted),
              uploaded_at: row.uploaded_at,
              id: row.id,
            }))
          )
        : null

      // Досье: нет Архива сущности. Цепочка R7 — docparser /info по dockey,
      // как versions у PL «Нормативный документ» из Архива карточки.
      let chainVersions = null
      if (!archiveVersions && meta?.model === 'finder') {
        const dockey =
          primary?.dockey || fromDp?.dockey || fromBe?.dockey || route.query.dockey
        let fromDockey = null
        if (dockey) {
          try {
            fromDockey = await fetchDocparserByDockey(String(dockey))
          } catch (error) {
            console.error(
              '[soykajs] не удалось получить версии по dockey',
              dockey,
              error
            )
            fromDockey = null
          }
        }
        if (gen !== loadGeneration) {
          return
        }
        chainVersions = mergeVersionSources(fromDockey, fromDp, primary)
      }

      const beWithArchive = {
        ...(fromBe || {}),
        versions: archiveVersions || chainVersions || fromBe?.versions,
      }

      const latest = newerLatestFilename(beWithArchive.versions, filename)
      const stillHere =
        router.currentRoute.value.name === 'soykajs.docview' &&
        String(router.currentRoute.value.params.filename) === String(filename)
      if (latest && stillHere) {
        // watch по props.filename перезагрузит просмотрщик уже с актуальной версией
        void router.replace({
          name: 'soykajs.docview',
          params: { filename: latest },
          query: route.query,
        })
        return
      }

      model.value = normalizeUpload(
        { ...primary, secure_filename: filename },
        filename,
        beWithArchive
      )
      return
    }

    await sleep(300)
  }
}

function normalizeUploadMetadata(raw) {
  if (!raw) {
    return {}
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw || '{}') || {}
    } catch (error) {
      console.error('[soykajs] не удалось разобрать metadata загрузки', error)
      return {}
    }
  }
  return typeof raw === 'object' ? raw : {}
}

watch(
  () => [props.filename, route.query._, route.query.dockey, user.value?.access_token],
  () => {
    if (!user.value?.access_token) {
      return
    }
    void loadUpload()
  },
  { immediate: true }
)

/**
 * Сиротские backdrop после пароля/сообщения поверх просмотрщика:
 * клики по сайдбару не доходят, /docview без окна — белый экран.
 */
function scrubOrphanModalDom() {
  if (document.querySelector('.modal.show')) {
    return
  }
  document.querySelectorAll('.modal-backdrop').forEach(node => node.remove())
  document.body.classList.remove('modal-open')
  document.documentElement.classList.remove('modal-open')
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('padding-right')
}

function isDocShellRoute(name) {
  return (
    name === 'soykajs.docview' ||
    name === 'soykajs.docedit' ||
    name === 'soykajs.docedit.mention'
  )
}

/** Явный уход с пустого /docview, если back по истории не вывел из оболочки. */
function escapeDocShell() {
  scrubOrphanModalDom()
  if (!isDocShellRoute(router.currentRoute.value.name)) {
    return
  }
  void router.replace('/').catch(() => {})
}

/**
 * Выход с /docview: один или несколько back, пока не уйдём с просмотрщика/редактора.
 * Нужно из‑за старых стеков (раньше при смене версии в dropdown пушили
 * второй /docview) — иначе Close возвращает в предыдущий viewer / пустой экран.
 */
function clearLeaveDocShellTimer() {
  if (!leaveDocShellTimer) {
    return
  }
  clearTimeout(leaveDocShellTimer)
  leaveDocShellTimer = 0
}

function onClose() {
  if (leavingViewer) {
    return
  }
  leavingViewer = true
  scrubOrphanModalDom()

  let hops = 0
  const maxHops = 8

  const removeHook = router.afterEach(to => {
    hops += 1
    if (isDocShellRoute(to.name)) {
      // Раньше при hops === maxHops хук просто снимался — оставались на пустом /docview.
      if (hops < maxHops && router.options.history.state.back) {
        router.back()
        return
      }
      removeHook()
      clearLeaveDocShellTimer()
      escapeDocShell()
      return
    }
    removeHook()
    clearLeaveDocShellTimer()
    scrubOrphanModalDom()
  })

  // Если afterEach не сработал (отмена навигации / гонка с паролем) — не зависаем.
  clearLeaveDocShellTimer()
  leaveDocShellTimer = window.setTimeout(() => {
    leaveDocShellTimer = 0
    removeHook()
    escapeDocShell()
  }, 800)

  if (!router.options.history.state.back) {
    removeHook()
    clearLeaveDocShellTimer()
    escapeDocShell()
    return
  }
  router.back()
}

onBeforeUnmount(() => {
  clearLeaveDocShellTimer()
  scrubOrphanModalDom()
})
</script>

<template>
  <!-- use-history false: маршрут /docview уже есть в истории. Лишний pushState
       плюс router.back() в onClose давали два history.back() и модалка иногда оставалась. -->
  <!-- ?readonly=1: открыли из досье файл только для чтения (ACL узла) — без карандаша. -->
  <Modal
    v-if="readyUpload"
    :key="viewerKey"
    :upload="readyUpload"
    :versioning="true"
    :use-history="false"
    :editable="route.query.readonly !== '1'"
    @hidden="onClose"
  />
</template>
